import type { ComponentType } from 'react';
import {
  ClipboardList,
  FlaskConical,
  Folder,
  GraduationCap,
  LayoutDashboard,
  Receipt,
  SlidersHorizontal,
  Truck,
  UserCog,
  Users,
} from 'lucide-react';
import type { Principal } from '@/models/principal';
import { resolveHomeRouteForRole } from '@/models/role';
import type appDe from '@/app/i18n/de.json';

/**
 * Dotted `app` namespace key for a top-navigation label or badge text — shared by all three
 * `AppTopNav` item sets ({@link CUSTOMER_TOP_NAV_ITEMS}, {@link DELABO_LAB_TOP_NAV_ITEMS},
 * {@link PORTAL_TOP_NAV_ITEMS}).
 */
type TopNavI18nKey = `topNav.${keyof typeof appDe.topNav}`;

/**
 * A dynamic counter badge on a nav entry: which count feeds it ({@link source}, the key
 * `AppTopNav`'s `badges` prop is looked up under) and how it is worded and capped.
 *
 * Deliberately data-free and free of any one feature's vocabulary — the i18n keys and the cap are
 * per-badge configuration, not baked into `NavBadge` — so a second badge (say, unread messages on
 * "Dateiablage") is a new constant here rather than another branch inside the component
 * (MR !171 review, Dennis Stojanov).
 */
export interface TopNavBadgeDef {
  /** Key into `AppTopNav`'s `badges` record supplying this badge's count. */
  readonly source: string;
  /** Screenreader text for an uncapped count; interpolated with `{{count}}`. */
  readonly labelKey: TopNavI18nKey;
  /** Screenreader text once {@link max} is exceeded; interpolated with `{{max}}`. */
  readonly cappedLabelKey: TopNavI18nKey;
  /** Highest number shown as-is; above it the badge reads `<max>+`. Defaults to 99 in `NavBadge`. */
  readonly max?: number;
}

/** Identifier of a top-navigation action; `AppTopNav`'s `actions` prop is keyed by it. */
export type TopNavActionId = 'datextLinkout';

interface TopNavItemBase {
  readonly labelKey: TopNavI18nKey;
  readonly icon: ComponentType<{ readonly className?: string }>;
}

/** A route-navigating {@link AppTopNav} entry: target route, label, icon, optional counter badge. */
export interface TopNavLinkDef extends TopNavItemBase {
  readonly kind: 'link';
  readonly to: string;
  /** Which dynamic badge (if any) this entry carries — see {@link TopNavBadgeDef}. */
  readonly badge?: TopNavBadgeDef;
}

/**
 * An {@link AppTopNav} entry that triggers behavior instead of navigating (e.g. opening an
 * external system in a new tab). Rendered only when the matching `actions` state is visible.
 */
export interface TopNavActionDef extends TopNavItemBase {
  readonly kind: 'action';
  readonly action: TopNavActionId;
}

/** One {@link AppTopNav} entry: a route link or an action. */
export type TopNavItemDef = TopNavLinkDef | TopNavActionDef;

/**
 * The open-Freigaben counter on "Kunden" (#573) — the number of customer organizations awaiting
 * partner-lab confirmation by the caller's own DELABO lab, fed by `useOpenAssignmentCount`.
 *
 * Declared only on {@link DELABO_LAB_TOP_NAV_ITEMS}, never on {@link PORTAL_TOP_NAV_ITEMS}: the
 * count endpoint is scoped to the caller's own DELABO lab and answers 403 for a Portal role, so
 * the badge is meaningless there (PO decision: DELABO Labor only). Enforcing that through the item
 * set rather than through the hook's `enabled` gate keeps it true even if the gate is ever widened.
 */
const OPEN_FREIGABEN_BADGE: TopNavBadgeDef = {
  source: 'openFreigaben',
  labelKey: 'topNav.openFreigabenBadge',
  cappedLabelKey: 'topNav.openFreigabenBadgeCapped',
};

/**
 * The unread-Dateiablage-documents counter on "Dateiablage" (e16-02), fed by
 * `useUngelesenZaehler`'s `gesamt` field — the number of unread files across every correspondent
 * of the caller's own organization.
 *
 * Passed to {@link moduleItems} for the customer and DELABO-Labor sets ({@link
 * CUSTOMER_MODULE_ITEMS}, {@link DELABO_LAB_MODULE_ITEMS}), never for the Portal one: Portal
 * support has no notion of "own organization" for Dateiablage purposes, matching `useUngelesenZaehler`'s own `PARTEI_ORG_TYPES` fetch-avoidance
 * gate (`useUngelesenZaehler.ts`). `max: 10` is this badge's own cap (e16-02 AK-2: `10+` from 11),
 * distinct from `NavBadge`'s default of 99.
 */
const DATEIABLAGE_UNGELESEN_BADGE: TopNavBadgeDef = {
  source: 'dateiablageUngelesen',
  labelKey: 'topNav.dateiablageUngelesenBadge',
  cappedLabelKey: 'topNav.dateiablageUngelesenBadgeCapped',
  max: 10,
};

/**
 * The module entries every audience shares, in the #572 order — appended after each set's own
 * audience-specific head so the common tail cannot drift between the three sets. Most have no real
 * target route yet, so each points at its own `/coming-soon/<bereich>` placeholder path: a real,
 * clickable, `NavLink`-navigable route, never an inert placeholder (accessibility: a focusable
 * link must do something when activated). A dedicated path per entry — rather than the shared
 * `/coming-soon` all four used to point at — is what keeps `NavLink`'s path-prefix active-state
 * detection unambiguous: with a shared target, every entry pointing at it was marked active at
 * once (INC-KW42-14). "Dateiablage" (`topNav.documents`) targets `/dateiablage` — the
 * file-exchange list page — for every audience.
 *
 * "Verwaltung" (`/coming-soon/verwaltung`) is a placeholder for the approved
 * Verwaltungs-Weiterleitseite (Datenablage, DATEXT/KVA, Benutzer — customer review 27.08.2026),
 * which is its own story; it is not a container for the Portal-audience entries below.
 *
 * Two audience-dependent aspects are parameters rather than per-set copies of the list, so the one
 * tail stays the single source of all three sets:
 *
 * <ul>
 *   <li>{@link ModuleItemsOptions.dateiablageBadge} — the "Dateiablage" unread counter (e16-02),
 *       carried by the customer and DELABO-Labor sets but not the Portal one, the same pattern
 *       {@link OPEN_FREIGABEN_BADGE} already uses for "Kunden".</li>
 *   <li>{@link ModuleItemsOptions.logistikTo} — "Logistik" stays on its placeholder
 *       (`/coming-soon/logistik`) only for the fail-closed fallback (an unrecognized/future
 *       organisation type and the `null` principal);
 *       every organisation type `LogistikUebersichtRoute`'s guard admits — DELABO Labor, DELABO
 *       Portal, Praxis, Kundenlabor — gets the real Logistik area screen instead.</li>
 * </ul>
 *
 * The two are independent: the Portal set takes the area screen *without* the badge, the
 * fail-closed fallback the badge *without* the area screen.
 */
function moduleItems({
  dateiablageBadge,
  logistikTo,
  invoices,
}: ModuleItemsOptions): readonly TopNavItemDef[] {
  // Conditional spread, not `badge: dateiablageBadge`: `exactOptionalPropertyTypes` rejects an
  // explicit `undefined` for the optional prop (same reasoning as `renderBadge`'s `maxProp` in
  // AppTopNav.tsx).
  const badgeProp = dateiablageBadge !== undefined ? { badge: dateiablageBadge } : {};

  return [
    {
      kind: 'link',
      to: '/coming-soon/verwaltung',
      labelKey: 'topNav.administration',
      icon: SlidersHorizontal,
    },
    { kind: 'link', to: '/dateiablage', labelKey: 'topNav.documents', icon: Folder, ...badgeProp },
    { kind: 'link', to: logistikTo, labelKey: 'topNav.logistics', icon: Truck },
    ...invoicesItem(invoices),
    { kind: 'link', to: '/coming-soon/akademie', labelKey: 'topNav.academy', icon: GraduationCap },
  ];
}

/** The per-audience knobs of {@link moduleItems}. */
interface ModuleItemsOptions {
  /** The "Dateiablage" unread-counter badge, or omitted for an audience that carries none. */
  readonly dateiablageBadge?: TopNavBadgeDef;
  /**
   * Target route of the "Logistik" entry — the `/coming-soon/logistik` placeholder or the real
   * area screen `/logistik`.
   */
  readonly logistikTo: string;
  /** How the "Rechnungen / KVA" entry is realized for this audience. */
  readonly invoices: InvoicesEntry;
}

/**
 * Realization of "Rechnungen / KVA": the DATEXT linkout action (customers; shown only when the
 * action state says so), the `/coming-soon/rechnungen` placeholder (DELABO Labor), or no entry
 * (DELABO Portal).
 */
type InvoicesEntry = 'datextLinkout' | 'placeholder' | 'none';

function invoicesItem(entry: InvoicesEntry): readonly TopNavItemDef[] {
  switch (entry) {
    case 'datextLinkout':
      return [
        { kind: 'action', action: 'datextLinkout', labelKey: 'topNav.invoices', icon: Receipt },
      ];
    case 'placeholder':
      return [
        { kind: 'link', to: '/coming-soon/rechnungen', labelKey: 'topNav.invoices', icon: Receipt },
      ];
    case 'none':
      return [];
  }
}

/**
 * The module tail of the fail-closed fallback set: the "Dateiablage" badge, but "Logistik" still
 * on the `/coming-soon/logistik` placeholder — the only set that keeps it, now that every
 * organisation type `LogistikUebersichtRoute`'s guard admits gets the real area screen (see
 * {@link CUSTOMER_LOGISTIK_MODULE_ITEMS}).
 */
const CUSTOMER_MODULE_ITEMS: readonly TopNavItemDef[] = moduleItems({
  dateiablageBadge: DATEIABLAGE_UNGELESEN_BADGE,
  logistikTo: '/coming-soon/logistik',
  invoices: 'datextLinkout',
});

/**
 * The module tail of the recognized Praxis/Kundenlabor set: the same badge as
 * {@link CUSTOMER_MODULE_ITEMS}, plus the real Logistik area screen `/logistik` as the customer
 * self-service entry point (#440 Nachbesserung 1).
 */
const CUSTOMER_LOGISTIK_MODULE_ITEMS: readonly TopNavItemDef[] = moduleItems({
  dateiablageBadge: DATEIABLAGE_UNGELESEN_BADGE,
  logistikTo: '/logistik',
  invoices: 'datextLinkout',
});

/**
 * The module tail of the DELABO-Labor set: the "Dateiablage" badge plus the real Logistik area
 * screen `/logistik` (Pencil node `Hed0T`). Both DELABO audiences land on that same area screen,
 * from which "Versanddienstleister verwalten" leads on to the Lieferdienstverwaltung; the Portal
 * audience will later also see the Versandauftrags-Übersicht there.
 *
 * `LogistikUebersichtRoute`'s own guard (a fail-closed role×organisation-type check per sicht) is
 * what actually keeps an unexpected principal out even on a direct URL visit
 * (AK-9) — this swap only decides which target the nav item *itself* links to, so a principal
 * outside that guard is never even offered a link that would only ever show `AccessDenied`.
 * `AppTopNav`'s `NavLink` marks "Logistik" active on `/logistik` and every
 * `/logistik/*` sub-route (e.g. `/logistik/lieferdienstverwaltung`), so it stays highlighted after
 * following the area screen's "Versanddienstleister verwalten" button.
 */
const DELABO_LAB_MODULE_ITEMS: readonly TopNavItemDef[] = moduleItems({
  dateiablageBadge: DATEIABLAGE_UNGELESEN_BADGE,
  logistikTo: '/logistik',
  invoices: 'placeholder',
});

/**
 * The module tail of the DELABO-Portal set: the same `/logistik` area screen as for DELABO-Labor
 * (see {@link DELABO_LAB_MODULE_ITEMS}), but without the "Dateiablage" badge — see
 * {@link DATEIABLAGE_UNGELESEN_BADGE}.
 */
const PORTAL_MODULE_ITEMS: readonly TopNavItemDef[] = moduleItems({
  logistikTo: '/logistik',
  invoices: 'none',
});

/**
 * "Übersicht", always the first entry of every set (PO decision; the #572 order applies from
 * "Aufträge" onward). Its target is **derived**, never written per set: `resolveHomeRouteForRole`
 * is the same single source of truth the post-login redirect, `DashboardRoute`'s own guard and
 * `RequireAnyRole`'s `redirectToRoleHome` read, so this link can never point somewhere the
 * principal is then bounced off.
 *
 * That derivation is the fix for MR !171's review finding: a hard-coded `/support-dashboard` on the
 * shared lab/portal set sent every DELABO-Labor principal into `SupportDashboardRoute`'s
 * `RequireAnyRole` (`PORTAL_ADMIN`/`PORTAL_SUPERUSER`) and therefore onto `AccessDenied`. Portal
 * roles resolve to `/support-dashboard`; every other role — including all DELABO-lab roles —
 * resolves to `/`, where `DashboardRoute` renders the DELABO-Labor variant of the dashboard.
 */
function overviewItem(principal: Principal | null): TopNavItemDef {
  return {
    kind: 'link',
    to: resolveHomeRouteForRole(principal?.role),
    labelKey: 'topNav.overview',
    icon: LayoutDashboard,
  };
}

/**
 * Customer (Praxis/Kundenlabor) main-navigation items from "Aufträge" onward — Pencil node
 * `TI0LX`, "Header Nav Row" (Kunden-Dashboard variant). "Aufträge" links to the unfiltered
 * Auftragsübersicht (`/auftraege`, no `fachbereich` query); the three former Fachbereich
 * deep-links (`?fachbereich=ORTHO|PROSTHETICS|IMPLANT`) were folded into this single entry (#572).
 * Fachbereiche remain reachable via the existing `FachbereichFilter` on the Auftragsübersicht, and
 * old deep-links carrying the `fachbereich` query keep working unchanged (`AuftraegeRoute` still
 * reads it). "Aufträge" stays marked active in the Top-Nav no matter which query ends up on
 * `/auftraege` — e.g. `?fachbereich=…` set by `FachbereichFilter` or a dashboard tile — because
 * `TopNavLink`'s active state is path-only (`NavLink` `end`).
 */
const CUSTOMER_ITEMS_AFTER_OVERVIEW: readonly TopNavItemDef[] = [
  { kind: 'link', to: '/auftraege', labelKey: 'topNav.orders', icon: ClipboardList },
  { kind: 'link', to: '/benutzer', labelKey: 'topNav.users', icon: UserCog },
  ...CUSTOMER_MODULE_ITEMS,
];

/**
 * {@link CUSTOMER_ITEMS_AFTER_OVERVIEW} for a recognized Praxis/Kundenlabor principal, with
 * "Logistik" pointing at the real `/logistik` area screen (see
 * {@link CUSTOMER_LOGISTIK_MODULE_ITEMS}).
 */
const CUSTOMER_LOGISTIK_ITEMS_AFTER_OVERVIEW: readonly TopNavItemDef[] = [
  { kind: 'link', to: '/auftraege', labelKey: 'topNav.orders', icon: ClipboardList },
  { kind: 'link', to: '/benutzer', labelKey: 'topNav.users', icon: UserCog },
  ...CUSTOMER_LOGISTIK_MODULE_ITEMS,
];

/**
 * DELABO-Labor main-navigation items from "Aufträge" onward: the customer set plus "Kunden"
 * (`/kunden`, the shared customer-overview list), which carries the #573 open-Freigaben badge.
 * "Aufträge" reuses `/auftraege`, which already renders this org type's Sicht. "Logistik" points
 * at the real `/logistik` area screen (see {@link DELABO_LAB_MODULE_ITEMS}).
 */
const DELABO_LAB_ITEMS_AFTER_OVERVIEW: readonly TopNavItemDef[] = [
  { kind: 'link', to: '/auftraege', labelKey: 'topNav.orders', icon: ClipboardList },
  {
    kind: 'link',
    to: '/kunden',
    labelKey: 'topNav.customers',
    icon: Users,
    badge: OPEN_FREIGABEN_BADGE,
  },
  { kind: 'link', to: '/benutzer', labelKey: 'topNav.users', icon: UserCog },
  ...DELABO_LAB_MODULE_ITEMS,
];

/**
 * DELABO-Portal (Support/Portal-Admin) main-navigation items from "Aufträge" onward — Pencil node
 * `q0dtCl`, "Header Nav Row" (Support-Dashboard variant, frame `ehpwz`). Like the DELABO-Labor set
 * but with "Labore" (`/labore`, the DELABO-Portal laboratory-overview list; its own
 * `RequireAnyRole` gate lives inside `LaboratoriesRoute`) and **without** the open-Freigaben badge
 * — see {@link OPEN_FREIGABEN_BADGE} — nor the "Dateiablage" unread-counter badge — see
 * {@link DATEIABLAGE_UNGELESEN_BADGE}. Portal support has no notion of "own organization" for
 * either count, matching the respective hooks' own fetch-avoidance gates.
 *
 * "Labore" is restored here after MR !171's first iteration dropped it while merging the lab and
 * portal sets: `/labore` is a real, implemented route whose only entry point is this item, so
 * losing the entry made the page reachable by direct URL entry alone. The Portal audience's other
 * historical entries ("Auftragsrouting", "Produktverwaltung") stay out, as decided for this MR —
 * both only ever pointed at a `/coming-soon` placeholder, and #572's role table does not list them.
 * Note that table also omits "Labore", "Statistik" and "Systemlog", while TC-E07-01-008 (#216)
 * and the legacy Admin-Ansicht list all of them: the Support row of #572 needs a follow-up
 * correction, tracked separately from this fix.
 *
 * "Logistik" points at the same `/logistik` area screen as for DELABO-Labor (see
 * {@link PORTAL_MODULE_ITEMS}).
 */
const PORTAL_ITEMS_AFTER_OVERVIEW: readonly TopNavItemDef[] = [
  { kind: 'link', to: '/auftraege', labelKey: 'topNav.orders', icon: ClipboardList },
  { kind: 'link', to: '/kunden', labelKey: 'topNav.customers', icon: Users },
  { kind: 'link', to: '/labore', labelKey: 'topNav.labs', icon: FlaskConical },
  { kind: 'link', to: '/benutzer', labelKey: 'topNav.users', icon: UserCog },
  ...PORTAL_MODULE_ITEMS,
];

/**
 * Resolves the top-navigation item set for a given principal (#572): an organisation-type based
 * selector that replaced the former role-allowlist check, now yielding **three** distinct sets
 * rather than two — merging the DELABO-Labor and DELABO-Portal audiences into one shared set was
 * what produced both of MR !171's review findings (wrong "Übersicht" target for labs, lost
 * "Labore" entry for Portal roles).
 *
 * `DELABO_LAB` gets {@link DELABO_LAB_ITEMS_AFTER_OVERVIEW} (with the badge-carrying "Kunden"),
 * `DELABO_PORTAL` gets {@link PORTAL_ITEMS_AFTER_OVERVIEW} (with "Labore", without the badge),
 * `PRACTICE`/`CUSTOMER_LAB` get the customer set with the real "Logistik" target
 * ({@link CUSTOMER_LOGISTIK_ITEMS_AFTER_OVERVIEW}), and every other organisation type — an
 * unrecognized/future value, and the `null` principal during the auth boot window — falls back to
 * the plain customer set with the `/coming-soon/logistik` placeholder, matching today's
 * fail-closed default.
 * "Übersicht" is prepended per principal by {@link overviewItem}.
 */
export function resolveTopNavItems(principal: Principal | null): readonly TopNavItemDef[] {
  const overview = overviewItem(principal);

  switch (principal?.organizationType) {
    case 'DELABO_LAB':
      return [overview, ...DELABO_LAB_ITEMS_AFTER_OVERVIEW];
    case 'DELABO_PORTAL':
      return [overview, ...PORTAL_ITEMS_AFTER_OVERVIEW];
    case 'PRACTICE':
    case 'CUSTOMER_LAB':
      return [overview, ...CUSTOMER_LOGISTIK_ITEMS_AFTER_OVERVIEW];
    default:
      return [overview, ...CUSTOMER_ITEMS_AFTER_OVERVIEW];
  }
}
